import { AlertTriangle, ArrowDown, ArrowUp, Copy } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";

import type { DupSerialRow } from "@busright/data-contract";

import { ChartCard } from "@/components/ChartCard";
import { EmptyState } from "@/components/EmptyState";
import { formatInt, relativeFromNow } from "@/lib/format";

type SortKey = "serial" | "occurrences" | "distinctDeviceIds" | "distinctGroups" | "latestLastSeen";
type SortDir = "asc" | "desc";

const compareRows = (a: DupSerialRow, b: DupSerialRow, key: SortKey): number => {
  if (key === "occurrences") {
    return a.occurrences - b.occurrences;
  }
  if (key === "distinctDeviceIds") {
    return a.distinctDeviceIds - b.distinctDeviceIds;
  }
  if (key === "distinctGroups") {
    return a.distinctGroups - b.distinctGroups;
  }
  if (key === "latestLastSeen") {
    return (a.latestLastSeen ?? "").localeCompare(b.latestLastSeen ?? "");
  }
  return a.serial.localeCompare(b.serial);
};

interface Header {
  readonly id: string;
  readonly key: SortKey;
  readonly label: string;
  readonly num?: boolean;
}
const HEADERS: readonly Header[] = [
  { id: "serial", key: "serial", label: "Serial" },
  { id: "occurrences", key: "occurrences", label: "Occurrences", num: true },
  { id: "distinctDeviceIds", key: "distinctDeviceIds", label: "Distinct device ids", num: true },
  { id: "distinctGroups", key: "distinctGroups", label: "Distinct groups", num: true },
  { id: "latestLastSeen", key: "latestLastSeen", label: "Latest last-seen" },
];

interface ChipProps {
  readonly label: string;
  readonly value: number;
  readonly color?: string;
}
const Chip = ({ label, value, color }: ChipProps): ReactNode => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 14px",
      background: "var(--br-surface-2)",
      border: "1px solid var(--br-border)",
      borderRadius: "var(--br-radius-md)",
    }}
  >
    <span
      className="br-tabular-nums"
      style={{ fontSize: 18, fontWeight: 700, color: color ?? "var(--br-text-primary)" }}
    >
      {value}
    </span>
    <span style={{ fontSize: 12, color: "var(--br-text-muted)" }}>{label}</span>
  </div>
);

const SortIcon = ({
  active,
  dir,
}: {
  readonly active: boolean;
  readonly dir: SortDir;
}): ReactNode => {
  if (!active) {
    return null;
  }
  return dir === "asc" ? <ArrowUp size={11} aria-hidden /> : <ArrowDown size={11} aria-hidden />;
};

// A short, joined preview of an id list — the full list stays in the `title` tooltip so the cell
// never overflows even when a serial spans many device ids / groups.
const ID_PREVIEW_COUNT = 2;
const ID_PREVIEW_CHARS = 8;
const IdList = ({ ids }: { readonly ids: readonly string[] }): ReactNode => {
  if (ids.length === 0) {
    return <span style={{ color: "var(--br-text-muted)" }}>—</span>;
  }
  const preview = ids.slice(0, ID_PREVIEW_COUNT).map((id) => id.slice(0, ID_PREVIEW_CHARS));
  const extra = ids.length - preview.length;
  return (
    <span className="mono" title={ids.join(", ")}>
      {preview.join(", ")}
      {extra > 0 ? ` +${extra} more` : ""}
    </span>
  );
};

export interface DsDupSerialsProps {
  readonly rows: readonly DupSerialRow[];
  readonly duplicateSerialCount: number;
  readonly duplicateRowCount: number;
  readonly blankSerialCount: number;
  readonly loading?: boolean;
}

// Section — Esper serial data-quality (R12). `serial` is the universal join key across the fleet
// roster / snapshot / reporting bridges, so a duplicated or blank serial silently corrupts every
// downstream join keyed on it. This is 100% real (GROUP BY serial HAVING COUNT>1) — no inference —
// framed explicitly as a cleanup worklist for Esper, not a health/RAG verdict.
export const DsDupSerials = ({
  rows,
  duplicateSerialCount,
  duplicateRowCount,
  blankSerialCount,
  loading,
}: DsDupSerialsProps): ReactNode => {
  const [sortKey, setSortKey] = useState<SortKey>("occurrences");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const sortedRows = useMemo(() => {
    const sorted = [...rows].sort((a, b) => compareRows(a, b, sortKey));
    return sortDir === "asc" ? sorted : sorted.reverse();
  }, [rows, sortKey, sortDir]);

  const onSort = (key: SortKey): void => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir(key === "serial" ? "asc" : "desc");
  };

  return (
    <ChartCard
      title="Duplicate & blank serials"
      subtitle="Esper serial is the universal join key — a duplicate or blank serial corrupts every downstream join keyed on it. Review for cleanup."
      source="100% real · GROUP BY serial HAVING COUNT>1"
      loading={loading}
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
        <Chip label="duplicate serials" value={duplicateSerialCount} color="var(--br-accent)" />
        <Chip label="rows spanned by duplicates" value={duplicateRowCount} />
        <Chip label="blank serials" value={blankSerialCount} color="var(--br-accent)" />
      </div>

      {rows.length === 0 ? (
        <EmptyState
          icon={Copy}
          title="No duplicate or blank serials"
          message="The latest Esper snapshot scan found nothing to clean up."
        />
      ) : (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              marginBottom: 12,
              padding: "8px 12px",
              background: "var(--br-surface-2)",
              border: "1px solid var(--br-border)",
              borderRadius: "var(--br-radius-md)",
              fontSize: 12,
              color: "var(--br-text-secondary)",
            }}
          >
            <AlertTriangle size={14} aria-hidden style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              These rows are flagged for manual Esper cleanup, not auto-resolved — dedup by serial
              before trusting any serial-keyed join downstream.
            </span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Issue</th>
                  {HEADERS.map((h) => {
                    const active = h.key === sortKey;
                    return (
                      <th key={h.id} className={h.num ? "num" : undefined}>
                        <button
                          type="button"
                          onClick={() => onSort(h.key)}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            padding: 0,
                            font: "inherit",
                            color: active ? "var(--br-text-primary)" : "inherit",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                          }}
                        >
                          {h.label}
                          <SortIcon active={active} dir={sortDir} />
                        </button>
                      </th>
                    );
                  })}
                  <th>Device ids</th>
                  <th>Groups</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row) => (
                  <tr key={`${row.issueType}-${row.serial}`}>
                    <td>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "3px 9px",
                          fontSize: 11,
                          fontWeight: 600,
                          color: "var(--br-accent)",
                          borderRadius: 999,
                          border: "1px solid var(--br-accent)",
                        }}
                      >
                        {row.issueType === "duplicate" ? "Duplicate" : "Blank"}
                      </span>
                    </td>
                    <td className="mono">{row.serial}</td>
                    <td className="num br-tabular-nums">{formatInt(row.occurrences)}</td>
                    <td className="num br-tabular-nums">{formatInt(row.distinctDeviceIds)}</td>
                    <td className="num br-tabular-nums">{formatInt(row.distinctGroups)}</td>
                    <td className="br-tabular-nums">
                      {row.latestLastSeen ? relativeFromNow(row.latestLastSeen) : "—"}
                    </td>
                    <td>
                      <IdList ids={row.deviceIds} />
                    </td>
                    <td>
                      <IdList ids={row.groupIds} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </ChartCard>
  );
};
